<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    v-loading="editProcessing"
    :visible.sync="editVisible"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Edit {{ editTypeHeader }}</h1>
        <div class="modal-actions col-2">
          <el-button
            type="primary"
            @click="editOperation"
            :disabled="numUsed || macUsed"
            >Save</el-button
          >
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
            >Cancel</el-button
          >
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          ></el-button>
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits service="Extension" :record="selectedExt" />
      <el-tabs v-model="activeTabName">
        <!-- General Tab -->
        <el-tab-pane label="General" name="General">
          <div v-if="editExtType === 'paging'">
            <div>
              <el-checkbox v-model="editForm.includeInDbn">
                Include in dial by name directory
              </el-checkbox>
              <el-checkbox v-model="editForm.includeInPb">
                Include in default phonebook
              </el-checkbox>
            </div>
            <div class="inline-form-group">
              <el-col :span="11">
                <el-input
                  placeholder="Enter account number"
                  v-model="editForm.accountNumber"
                >
                  <template slot="prefix"> Account number </template>
                </el-input>
              </el-col>
              <el-col :span="1" />
            </div>
            <div class="inline-form-group">
              <el-col :span="11">
                <el-input
                  v-model="editForm.extNumber"
                  placeholder="Extension number"
                >
                  <template slot="prefix">Extension number</template>
                  <template slot="append" v-if="numUsed">
                    <in-use :inUse="numInUse" />
                  </template>
                </el-input>
              </el-col>
              <el-col :span="1" />
              <el-col :span="12">
                <el-input
                  v-model="editForm.extName"
                  placeholder="Extension name"
                >
                  <template slot="prefix">Extension name</template>
                </el-input>
              </el-col>
            </div>
            <div class="inline-form-group">
              <el-col :span="11">
                <dads-select
                  v-model="editForm.emergencyAddress"
                  placeholder="Select e911 location"
                  v-loading="locationsProcessing"
                  :disabled="!locationOptions.length"
                  filterable
                >
                  <template slot="prefix">e911 Location</template>
                  <el-option
                    v-for="option in locationOptions"
                    :key="option.id"
                    :value="option.id"
                    :label="option.name"
                  />
                </dads-select>
              </el-col>
              <el-col :span="1" />
              <el-col :span="12">
                <el-input
                  v-model="editForm.emergencyLocation"
                  placeholder="e911 specific room/bldg/etc.."
                >
                  <template slot="prefix"> Location override </template>
                </el-input>
              </el-col>
            </div>
            <div class="inline-form-group">
              <div v-if="editForm.emergencyAddress">
                <el-tag class="address-tag">
                  <div>
                    <p class="address-label">
                    <strong>Address:</strong>
                    {{ address }}
                    <br/>
                    </p>
                    <p class="coordinates-label">
                    <strong>Lat/Long:</strong>
                    {{ latitude + ', ' + longitude }}
                    </p>
                  </div>
                </el-tag>
              </div>
            </div>
            <div class="flex align-flex-end pt-10">
              <h2 class="black-text mb-0 mt-0">Provisioning info</h2>
              <connection-info
                :data="deviceHelper"
                @credsGenerated="updatePeerSecret"
              />
            </div>
            <div class="inline-form-group">
              <h2 class="form-section-header mb-0" style="margin-left: 0">
                Online devices
              </h2>
              <el-button
                class="mt-10"
                slot="reference"
                type="primary"
                @click="queryRegistrations"
              >
                Load online devices
              </el-button>
            </div>
            <vxe-table
              round
              class="mt-10"
              border="inner"
              height="150px"
              :data="deviceTableData"
              :loading="deviceTableLoading"
              show-overflow
            >
              <vxe-table-column
                field="agent"
                title="User Agent"
                min-width="350"
                show-header-overflow
                show-overflow
              />
              <vxe-table-column
                field="address"
                title="Address"
                min-width="350"
                show-header-overflow
                show-overflow
              />
              <vxe-table-column
                field="expires"
                title="Expires"
                min-width="75"
                show-header-overflow
                show-overflow
              />
              <vxe-table-column
                field="received"
                title="Received"
                min-width="200"
                show-header-overflow
                show-overflow
              />
              <vxe-table-column
                field="path"
                title="Path"
                min-width="500"
                show-header-overflow
                show-overflow
              />
              <vxe-table-column
                field="lastKeepAlive"
                title="Last Keep Alive"
                min-width="150"
                show-header-overflow
                show-overflow
              />
              <vxe-table-column
                field="serverId"
                title="Server Id"
                min-width="75"
                show-header-overflow
                show-overflow
              />
            </vxe-table>
          </div>
          <div v-else>
            <el-checkbox
              v-model="editForm.dnd"
              :label="true"
              v-if="editExtType === 'desk'"
              style="margin-left: 0"
            >
              <span>Enable do not disturb</span>
            </el-checkbox>
            <el-checkbox
              v-model="editForm.mailbox"
              :label="true"
              v-if="editExtType === 'desk'"
              style="margin-left: 0"
            >
              <span>Enable voicemail message waiting indicator</span>
            </el-checkbox>
            <el-checkbox
              v-model="editForm.enableMeet"
              :label="true"
              v-if="editExtType === 'desk'"
              style="margin-left: 0"
            >
              <span>Enable video meetings</span>
            </el-checkbox>
            <el-checkbox
              v-model="editForm.enableTranscription"
              :label="true"
              v-if="editExtType === 'desk'"
              style="margin-left: 0"
            >
              <span>Enable call insights</span>
            </el-checkbox>
            <el-checkbox
              v-model="enable911Override"
              v-if="editExtType === 'sip' && isSuper"
              style="margin-left: 0"
            >
              <span>Enable e911 override</span>
            </el-checkbox>

            <div class="inline-form-group">
              <el-col :span="11">
                <el-input
                  v-model="editForm.extNumber"
                  placeholder="Extension number"
                >
                  <template slot="prefix">Extension number</template>
                  <template slot="append" v-if="numUsed">
                    <in-use :inUse="numInUse" />
                  </template>
                </el-input>
              </el-col>
              <el-col :span="1" />
              <el-col :span="12">
                <el-input
                  v-model="editForm.extName"
                  placeholder="Extension name"
                >
                  <template slot="prefix">Extension name</template>
                </el-input>
              </el-col>
            </div>
            <el-input
              class="checkbox-append"
              v-model="email"
              clearable
              placeholder="Enter email"
              v-if="editExtType === 'desk'"
            >
              <template slot="prefix">Email</template>
              <template slot="append">
                <el-checkbox
                  v-model="editForm.sendInvite"
                  :label="true"
                  v-if="editExtType === 'desk' && email !== ''"
                >
                  <span>Send invite</span>
                </el-checkbox>
              </template>
            </el-input>
            <div class="inline-form-group">
              <el-col :span="11">
                <dads-select
                  v-model="editForm.emergencyAddress"
                  placeholder="Select e911 location"
                  v-loading="locationsProcessing"
                  :disabled="!locationOptions.length"
                  filterable
                >
                  <template slot="prefix">e911 Location</template>
                  <el-option
                    v-for="option in locationOptions"
                    :key="option.id"
                    :value="option.id"
                    :label="option.name"
                  />
                </dads-select>
              </el-col>
              <el-col :span="1" />
              <el-col :span="12">
                <el-input
                  v-model="editForm.emergencyLocation"
                  placeholder="e911 specific room/bldg/etc.."
                >
                  <template slot="prefix"> Location override </template>
                </el-input>
              </el-col>
            </div>
            <div class="inline-form-group">
              <div v-if="editForm.emergencyAddress">
                <el-tag class="address-tag">
                  <div>
                    <p class="address-label">
                      <strong>Address:</strong>
                      {{ address }}
                      <br/>
                    </p>
                    <p class="coordinates-label">
                      <strong>Lat/Long:</strong>
                      {{ latitude + ', ' + longitude }}
                    </p>
                  </div>
                </el-tag>
              </div>
            </div>
            <div class="inline-form-group">
              <el-col :span="11">
                <dads-select
                  v-model="editForm.branchId"
                  :value="editForm.branchId"
                  placeholder="Select branch"
                  filterable
                >
                  <template slot="prefix">Branch</template>
                  <el-option
                    v-for="item in branchOptions"
                    :key="item + getRandomKey()"
                    :label="item.name"
                    :value="item.id"
                  />
                </dads-select>
              </el-col>
            </div>

            <div v-if="editExtType === 'desk'">
              <h2 class="form-section-header">Outbound recording</h2>
              <dads-select
                v-model="editForm.recording"
                placeholder="Select option"
              >
                <template slot="prefix"> Recording </template>
                <el-option
                  v-for="(item, index) in outboundRecordingOptions"
                  :key="index"
                  :label="item.label"
                  :value="item.value"
                />
              </dads-select>
              <el-tag class="mb-1">
                <i class="el-icon-info" style="margin-right: 4px" />
                To enable inbound call recording, edit the phone numbers
              </el-tag>

              <div
                v-if="
                  editForm.recording === 'yes' ||
                  editForm.recording === 'nochange' ||
                  editForm.recording === 'yeschange'
                "
              >
                <h2 class="form-section-header">Email recording</h2>
                <el-input
                  v-model="editForm.emailRecording"
                  placeholder="Enter emails"
                >
                  <template slot="prefix"> Emails </template>
                </el-input>
              </div>
            </div>

            <div v-if="editExtType === 'sip'">
              <div class="flex align-flex-end">
                <h2 class="black-text mb-0 mt-0">Authentication method</h2>
                <connection-info
                  :data="deviceHelper"
                  @credsGenerated="updatePeerSecret"
                />
              </div>
              <el-radio-group v-model="editForm.sipAuthType" class="mv-10">
                <el-radio-button label="auth">Normal auth</el-radio-button>
                <el-radio-button label="ipAuth">IP based auth</el-radio-button>
              </el-radio-group>
              <el-input
                v-model="editForm.sipAuthIp"
                placeholder="Enter public IP"
                v-if="editForm.sipAuthType === 'ipAuth'"
              >
                <template slot="prefix"> Public IP </template>
              </el-input>
            </div>

            <div v-if="editExtType === 'sip'">
              <h2 class="form-section-header">Call paths field</h2>
              <el-input
                v-model.number="editForm.callLimit"
                type="number"
                placeholder="Call paths field"
              >
                <template slot="prefix">Call Paths Field</template>
              </el-input>
              <h2 class="form-section-header mb-0">CID Source</h2>
              <dads-select
                v-model="editForm.trunkCidSource"
                placeholder="Select option"
              >
                <el-option
                  v-for="(item, index) in trunkCidSourceOptions"
                  :key="index"
                  :label="item.label"
                  :value="item.value"
                />
                <template slot="prefix">CID Source</template>
              </dads-select>
            </div>
            <h2 class="form-section-header">Outbound caller id</h2>
            <dads-select
              v-model="editForm.cidNum"
              :value="editForm.cidNum"
              clearable
              filterable
              allow-create
              remote
              :remote-method="remoteMethodForOutboundCid"
              placeholder="Select caller id"
            >
              <template slot="prefix">Ext caller id</template>
              <el-option
                v-for="option in outboundCidNumbersOptions"
                :key="option.label + getRandomKey()"
                :value="option.label"
                :label="option.label | formatNumber"
              />
            </dads-select>

            <div v-if="editForm.cidNums.length">
              <h2 class="form-section-header">Assigned numbers</h2>
              <el-select
                v-model="editForm.cidNums"
                :value="editForm.cidNums"
                :disabled="true"
                multiple
                placeholder=""
              >
                <template slot="prefix"
                  ><span style="opacity: 0">Joe Is Dad</span></template
                >
                <el-option
                  v-for="item in cidNumOptions"
                  :key="item.label + getRandomKey()"
                  :label="item.label | formatNumber"
                  :value="item.key"
                />
              </el-select>
              <el-tag class="mb-10">
                <i class="el-icon-info" style="margin-right: 4px"></i>
                These numbers directly ring the extension. To make changes,
                visit the phone numbers section.
              </el-tag>
            </div>

            <!-- ⭐ ADVANCED SETTINGS ⭐ -->
            <omnia-collapse
              title="Advanced"
              :active="collapseActive"
              @toggle="collapseActive = !collapseActive"
            >
              <el-checkbox v-model="editForm.includeInDbn">
                Include in dial by name directory
              </el-checkbox>
              <el-checkbox v-model="editForm.includeInPb">
                Include in default phonebook
              </el-checkbox>

              <div class="inline-form-group">
                <el-col :span="11">
                  <el-input
                    placeholder="Enter account number"
                    v-model="editForm.accountNumber"
                  >
                    <template slot="prefix"> Account number </template>
                  </el-input>
                </el-col>
                <el-col :span="1" />
                <el-col :span="12">
                  <el-input
                    v-model="editForm.department"
                    :value="editForm.department"
                    placeholder="Department"
                  >
                    <template slot="prefix">Department</template>
                  </el-input>
                </el-col>
              </div>

              <div class="inline-form-group">
                <el-col :span="11">
                  <el-select
                    v-model="rings"
                    :value="rings"
                    placeholder="Select max number of rings"
                  >
                    <template slot="prefix">Max inbound rings</template>
                    <el-option v-for="i in 15" :key="i" :value="i" :label="i" />
                  </el-select>
                </el-col>
                <el-col :span="1" />
                <el-col :span="12">
                  <el-select
                    v-model="editForm.muId"
                    placeholder="Select hold music"
                  >
                    <template slot="prefix">Hold music</template>
                    <el-option :value="0" label="Tenant default" />
                    <template v-if="editExtType === 'desk'">
                      <el-option
                        v-for="item in musicOnHoldsList"
                        :key="item.name + getRandomKey()"
                        :value="item.id"
                        :label="item.name"
                      />
                    </template>
                  </el-select>
                </el-col>
              </div>

              <div class="inline-form-group">
                <el-col :span="11">
                  <el-select v-model="editForm.callWaiting">
                    <template slot="prefix">Call waiting</template>
                    <el-option value="" label="Tenant default" />
                    <el-option value="yes" label="Always Dial" />
                    <el-option value="no" label="Check Status" />
                  </el-select>
                </el-col>
                <el-col :span="1" />
                <el-col :span="12">
                  <el-select v-model="editForm.rpid">
                    <el-option value="0" label="Tenant default" />
                    <el-option value="6" label="Default voice" />
                    <el-option value="9" label="Fax" />
                    <el-option value="7" label="International" />
                    <el-option value="36" label="International 2.0" />
                    <el-option value="33" label="Local International" />
                    <template slot="prefix">Routing profile</template>
                  </el-select>
                </el-col>
              </div>

              <h2 class="form-section-header">7-digit dialing</h2>
              <el-input v-model="editForm.prefix" placeholder="Your area code">
                <template slot="prefix">Default area code</template>
              </el-input>
              <el-tag class="mb-10">
                <i class="el-icon-info" style="margin-right: 4px"></i>
                This allows making calls without dialing the area code first
              </el-tag>
            </omnia-collapse>
          </div>
        </el-tab-pane>

        <!-- ⭐ DEVICES ⭐ -->
        <el-tab-pane
          label="Devices"
          name="Devices"
          v-if="editExtType !== 'sip' && editExtType !== 'paging'"
        >
          <div>
            <div class="flex align-flex-end">
              <h2 class="black-text mb-0 mt-0">Devices</h2>
              <connection-info
                :data="deviceHelper"
                @credsGenerated="updatePeerSecret"
              />
            </div>
            <!-- EXT HAS Device -->
            <div v-if="extHasDevice">
              <el-checkbox v-model="editForm.removeDevice" :label="true">
                <span>Remove Device</span>
              </el-checkbox>
              <div v-if="!editForm.removeDevice">
                <el-select
                  v-model="editForm.device.phPmId"
                  placeholder="Select phone model"
                >
                  <template slot="prefix">Phone model</template>
                  <el-option
                    v-for="(item, index) in deviceModelList"
                    :key="index"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
                <el-input
                  v-model="editForm.device.name"
                  placeholder="Enter device name"
                  readonly
                >
                  <template slot="prefix">Device name</template>
                </el-input>
                <!-- Mac Input Field with Mask -->
                <div class="el-input el-input--prefix">
                  <input
                    type="text"
                    autocomplete="off"
                    placeholder="Enter device MAC"
                    class="el-input__inner"
                    v-model="editForm.device.mac"
                    readonly
                    @input="
                      editForm.device.mac = $event.target.value.toUpperCase()
                    "
                    v-mask="'NN:NN:NN:NN:NN:NN'"
                  />
                  <span class="el-input__prefix">Device MAC</span>
                  <div v-if="macUsed" class="el-input-group__append">
                    <in-use :inUse="macInUse" />
                  </div>
                </div>
              </div>
            </div>
            <!-- EXT HAS NO DEVICE -->
            <div v-if="!extHasDevice || editForm.removeDevice">
              <div v-if="deviceList.length === 0">
                <el-button
                  @click="deviceType = 'New Device'"
                  class="mt-10 mb-10"
                >
                  <div class="dashboard-button-group">
                    <v-svg
                      class="voxo-pink"
                      sprite="devices"
                      style="width: 20px; height: 20px"
                    />
                    <span>New device</span>
                  </div>
                </el-button>
              </div>
              <div v-if="deviceList.length > 0">
                <dads-select
                  v-model="deviceType"
                  :value="deviceType"
                  placeholder="Select device"
                  filterable
                  clearable
                >
                  <template slot="prefix">Device</template>
                  <el-option value="Existing Device" label="Existing device" />
                  <el-option value="New Device" label="New device" />
                </dads-select>
              </div>
              <div v-if="deviceType === 'New Device'">
                <dads-select
                  v-model="editForm.newDevice.phPmId"
                  placeholder="Select phone model"
                  clearable
                >
                  <template slot="prefix">Phone model</template>
                  <el-option
                    v-for="(item, index) in deviceModelList"
                    :key="index"
                    :label="item.name"
                    :value="item.id"
                  />
                </dads-select>
                <el-input
                  v-model="editForm.newDevice.name"
                  placeholder="Enter device name"
                >
                  <template slot="prefix">Device Name</template>
                </el-input>
                <!-- Mac Input Field with Mask -->
                <div class="el-input el-input--prefix">
                  <input
                    type="text"
                    autocomplete="off"
                    placeholder="Enter device MAC"
                    class="el-input__inner"
                    :value="editForm.newDevice.mac"
                    @input="
                      editForm.newDevice.mac = $event.target.value.toUpperCase()
                    "
                    v-mask="'NN:NN:NN:NN:NN:NN'"
                    style="margin-top: 5px"
                  /><span class="el-input__prefix">Device MAC</span>
                  <div v-if="macInUse" class="el-input-group__append">
                    In Use!
                  </div>
                </div>
              </div>
              <div v-if="deviceType === 'Existing Device'">
                <dads-select
                  v-model="editForm.existingDevice.id"
                  :value="editForm.existingDevice.id"
                  placeholder="Select device"
                  :disabled="!deviceList.length"
                  clearable
                >
                  <template slot="prefix">Device</template>
                  <el-option
                    v-for="option in deviceList"
                    :key="option.id"
                    :value="option.id"
                    :label="option.name"
                  />
                </dads-select>
              </div>
            </div>
          </div>
          <div class="inline-form-group">
            <h2 class="form-section-header mb-0" style="margin-left: 0">
              Online devices
            </h2>
            <el-button
              slot="reference"
              type="primary"
              @click="queryRegistrations"
            >
              Load online devices
            </el-button>
          </div>
          <vxe-table
            round
            class="mt-10"
            border="inner"
            height="150px"
            :loading="deviceTableLoading"
            :data="deviceTableData"
            show-overflow
            empty-text="No Data"
          >
            <vxe-table-column
              field="agent"
              title="User Agent"
              min-width="350"
              show-header-overflow
              show-overflow
            />
            <vxe-table-column
              field="address"
              title="Address"
              min-width="350"
              show-header-overflow
              show-overflow
            />
            <vxe-table-column
              field="expires"
              title="Expires"
              min-width="75"
              show-header-overflow
              show-overflow
            />
            <vxe-table-column
              field="received"
              title="Received"
              min-width="200"
              show-header-overflow
              show-overflow
            />
            <vxe-table-column
              field="path"
              title="Path"
              min-width="500"
              show-header-overflow
              show-overflow
            />
            <vxe-table-column
              field="lastKeepAlive"
              title="Last Keep Alive"
              min-width="150"
              show-header-overflow
              show-overflow
            />
            <vxe-table-column
              field="serverId"
              title="Server Id"
              min-width="75"
              show-header-overflow
              show-overflow
            />
          </vxe-table>
        </el-tab-pane>

        <!-- ⭐ NEVER MISS A CALL ⭐ -->
        <el-tab-pane
          label="Never Miss A Call"
          name="Never Miss A Call"
          v-if="editExtType === 'desk'"
        >
          <el-checkbox
            v-model="forwardAllCalls"
            :label="true"
            style="margin-left: 0"
          >
            <span>Enable forwarding all calls</span>
          </el-checkbox>
          <!-- ⭐ Forwarding Destination ⭐ -->
          <div v-if="forwardAllCalls">
            <h2 class="form-section-header">Forward all calls</h2>
            <destination-selector
              title="Forward all calls to"
              :isTerminalDestSelector="true"
              :draggable="true"
              :draggableDestTypes="draggableDestTypes"
              :clearFields="clearFormFields"
              :selectedDestinations="editForm.forwardingDestinations"
              @destinationsSelected="onForwardingDestSelection"
            />
          </div>

          <!-- ⭐ Existing Destinations ⭐ -->
          <!-- EXT BUSY -->
          <h2 class="form-section-header">If busy</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
            :selectedDestinations="editForm.busyDestinations"
            @destinationsSelected="onBusyDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />
          <!-- EXT OFFLINE -->
          <h2 class="form-section-header">If offline</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
            :selectedDestinations="editForm.offlineDestinations"
            @destinationsSelected="onOfflineDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />
          <!-- EXT NO ANSWER -->
          <h2 class="form-section-header">If no answer</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
            :selectedDestinations="editForm.noAnswerDestinations"
            @destinationsSelected="onNoAnswerDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />
        </el-tab-pane>

        <!-- ⭐ FAXING ⭐ -->
        <el-tab-pane label="Faxing" name="faxing" v-if="editExtType === 'desk'">
          <div class="inline-form-group">
            <el-checkbox
              v-model="editForm.enableOutboundFaxing"
              :disabled="!email"
              style="margin-left: 0"
            >
              Enable outbound faxing
            </el-checkbox>
          </div>
          <el-tag
            class="mb-10"
            type="warning"
            v-if="editForm.enableOutboundFaxing"
          >
            <i class="el-icon-warning" style="margin-right: 4px"></i>
            Enabling this feature may cause you to travel back in time.
          </el-tag>

          <div class="inline-form-group">
            <el-checkbox
              v-model="editForm.faxDirect"
              style="margin-left: 0"
              :disabled="!email"
            >
              Enable Direct Fax

              <el-popover placement="right" trigger="hover" width="300">
                <span class="black-text">
                  This allows using a direct dial for fax or voice
                </span>
                <span slot="reference">
                  <i class="el-icon-question" style="margin-right: 4px"></i>
                </span>
              </el-popover>
            </el-checkbox>
          </div>

          <div v-if="editForm.enableOutboundFaxing">
            <h2 class="form-section-header">Fax outbound caller id</h2>
            <el-select
              v-model="editForm.faxCidNum"
              :disabled="!email"
              filterable
              allow-create
              clearable
              remote
              :remote-method="remoteFaxOutboundCid"
              placeholder="Select caller id"
            >
              <template slot="prefix">Fax caller id</template>
              <el-option
                v-for="option in faxOutboundCidOptions"
                :key="option.id + getRandomKey()"
                :value="option.label"
                :label="option.label | formatNumber"
              />
            </el-select>
          </div>
          <el-tag class="mb-10" type="warning" v-if="!email">
            <i class="el-icon-warning" style="margin-right: 4px"></i>
            To enable faxing features, an email must be provided.
          </el-tag>
        </el-tab-pane>
      </el-tabs>
    </el-form>
    <destination-references
      :tenantId="selectedExt ? selectedExt.tenantId : null"
      type="EXT"
      :id="selectedExt ? selectedExt.id : null"
      :clearState="clearFormFields"
    />
  </el-dialog>
</template>

<script>
import feathersClient from "../../../../feathers/feathers-client";
import DadsSelect from "@/components/select";
import ConnectionInfo from "./connectionInfo";
import RecentAudits from "@/components/RecentAudits";

import DestinationSelector from "@/components/DestinationSelector.vue";
import DestinationReferences from "@/components/DestinationReferences";
import InUse from "@/components/InUse";
import OmniaCollapse from "@/components/OmniaCollapse";
import {phoneNumberFormatter, isValidPhoneNumber} from "@/globals/globals";
import {debounce} from "lodash";

export default {
  name: "EditExtensionForm",
  props: {
    clearFormFields: {
      type: Boolean,
      required: true,
    },
    editProcessing: { required: true },
    editVisible: { required: true },
    numInUse: { required: true },
    macInUse: { required: true },
    locationsProcessing: { required: true },
    locationOptions: { required: true },
    branchOptions: { required: true },
    departmentOptions: { required: true },
    numberOptions: { required: true },
    deviceList: { required: true },
    faxOutboundCidOptions: { required: true },
    outboundCidNumbersOptions: { required: true },
    deviceModelList: { required: true },
    musicOnHoldsList: { required: true },
    selectedExt: { required: true },
    editExtType: { required: true },
    dncList: { required: true },
    outboundRecordingOptions: { required: true },
  },

  components: {
    DestinationSelector,
    DadsSelect,
    DestinationReferences,
    ConnectionInfo,
    RecentAudits,
    InUse,
    OmniaCollapse,
  },

  data() {
    return {
      collapseActive: false,
      deviceTableLoading: false,
      deviceTableData: [],
      activePeerName: "",
      activePeerSecret: "",
      extHasDevice: false,
      deviceType: "",
      cidNums: [],
      activeTabName: "General",
      rings: null,
      forwardAllCalls: false,
      email: "",
      emitCheckMac: false,
      initialMac: "",
      emitCheckExtNum: false,
      initialExtNum: "",
      emitCheckExtName: false,
      initialExtName: "",
      initialEmail: "",
      trunkCidSourceOptions: [
        { label: "PAI", value: "PAI" },
        { label: "RPID", value: "RPID" },
        { label: "FROM", value: "FROM" },
      ],
      enable911Override: false,
      editForm: {
        enableMeet: false,
        enableTranscription: false,
        mailbox: "",
        enableOutboundFaxing: false,
        faxDirect: false,
        faxCidNum: "",
        emergencyLocation: "",
        mailToFaxId: null,
        unconditionalStatus: "",
        recording: "",
        dnd: false,
        extNumber: "",
        extName: "",
        email: "",
        sendInvite: false,
        branchId: null,
        dialTimeout: null,
        cidNum: "",
        sipAuthType: "",
        sipAuthIp: "",
        callLimit: null,
        cidNums: [],
        cidNumsChanged: false,
        extHasDevice: false,
        trunkCidSource: "",
        device: {
          id: null,
          name: "",
          mac: "",
          phPmId: null,
        },
        newDevice: {
          name: "",
          mac: "",
          phPmId: null,
        },
        existingDevice: {
          id: null,
        },
        noAnswerDestinations: [],
        busyDestinations: [],
        offlineDestinations: [],
        forwardingDestinations: [],
        removeDevice: false,
        emergencyAddress: null,
        tenantId: null,
        // ⭐️ Advanced Settings ⭐️ //
        includeInDbn: false,
        includeInPb: false,
        accountNumber: "",
        department: "",
        prefix: "",
        doNotCalls: [],
        callWaiting: "",
        rpid: "",
        muId: "",
      },
      draggableDestTypes: [
        "SPECIAL",
        "CUSTOM",
        "IVR",
        "VOICEMAIL",
        "PLAYBACK",
        "HUNTLIST",
        "EXT",
        "QUEUE",
        "SMS",
        "MUSICONHOLD",
        "CONDITION",
      ],
    };
  },
  computed: {
    cidNumOptions() {
      return [...this.cidNums, ...this.numberOptions];
    },

    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    extensionNumber() {
      return this.editForm.extNumber;
    },

    extensionName() {
      return this.editForm.extName;
    },

    mac() {
      return this.editForm.device.mac;
    },

    locationInformation() {
      return this.locationOptions.find(x => +x.id === +this.editForm.emergencyAddress);
    },

    address() {
      return this.locationInformation.address.streetNum + ' ' + this.locationInformation.address.streetInfo + ', ' + this.locationInformation.address.city + ', ' + this.locationInformation.address.state + ' ' + this.locationInformation.address.postalCode;
    },

    latitude() {
      const latitude = this.locationInformation.address.loc.split(",")[0];
      return Number(latitude).toFixed(2);
    },

    longitude() {
      const longitude = this.locationInformation.address.loc.split(",")[1];
      return Number(longitude).toFixed(2);
    },

    editTypeHeader() {
      switch (this.editExtType) {
        case "desk":
          return "Unlimited Extension";
        case "sip":
          return "SIP Trunk";
        case "fax":
          return "Fax ATA";
      }
    },

    deviceHelper() {
      return [
        { key: "SIP Proxy", value: "sip.voxo.co" },
        { key: "DNS Type", value: "DNS SRV" },
        { key: "SIP Username", value: this.activePeerName },
        { key: "SIP Password", value: this.activePeerSecret },
        { key: "Register Expires", value: "120 sec" },
        { key: "Codec Support", value: "G711/G722/OPUS" },
      ];
    },

    macUsed() {
      return this.macInUse && this.macInUse.inUse;
    },

    numUsed() {
      return this.numInUse && this.numInUse.inUse;
    },
    user() {
      return this.$store.state.auth.user;
    },
    isSuper() {
      if (this.user) {
        return +this.user.userRole === 1;
      }
      return false;
    },
  },
  watch: {
    extensionNumber(value) {
      if (value !== "") {
        if (!this.emitCheckExtNum) {
          this.emitCheckExtNum = true;
          this.initialExtNum = value;
          return;
        }
        if (this.initialExtNum !== value) {
          this.$emit("checkNumber", value);
        }
      }
    },
    mac(value) {
      if (value !== "") {
        if (!this.emitCheckMac) {
          this.initialMac = value;
          this.emitCheckMac = true;
          return;
        }
        if (this.initialMac !== value) {
          this.$emit("checkMac", value);
        }
      }
    },
    extensionName: debounce(function (value) {
      if (value !== "") {
        this.editForm.extName = this.checkName(value);
      }
    }, 500),
    clearFormFields() {
      if (this.clearFormFields) {
        this.clearFields(this.editForm);
      }
    },

    email(value) {
      if (!value) {
        this.editForm.sendInvite = false;
        this.editForm.email = "";
      } else {
        this.editForm.email = value;
      }
    },

    selectedExt(value) {
      if (value) {
        const {
          tenantId,
          muId,
          prefix,
          device,
          dialTimeout,
          email,
          number,
          name,
          department,
          branchId,
          cidNum,
          cidNums,
          dnd,
          faxDirect,
          unconditionalStatus,
          emergencyAddress,
          recording,
          de_destinations,
          mailToFax,
          peerName,
          peerSecret,
          sipAuthType,
          sipAuthIp,
          callLimit,
          emergencyLocation,
          callWaiting,
          accountNumber,
          rpid,
          mailbox,
          enableMeet,
          enableTranscription,
          busyDestinations,
          offlineDestinations,
          noAnswerDestinations,
          forwardingDestinations,
          emailRecording,
          trunkEmergencyCidOverride,
          includeInDbn,
          includeInPb,
          trunkCidSource,
        } = this.selectedExt;

        this.editForm.includeInDbn = includeInDbn === "yes";
        this.editForm.includeInPb = includeInPb === "yes";
        this.editForm.enableTranscription = !!enableTranscription;
        this.editForm.emergencyAddress = emergencyAddress
          ? emergencyAddress
          : null;
        this.editForm.enableMeet = !!enableMeet;
        this.editForm.mailbox = !!mailbox;
        this.editForm.rpid = rpid.toString();
        this.editForm.tenantId = tenantId;
        this.editForm.callWaiting = callWaiting;
        this.editForm.accountNumber = accountNumber;
        this.editForm.emergencyLocation = emergencyLocation;
        this.editForm.sipAuthType = sipAuthType;
        this.editForm.sipAuthIp = sipAuthType === "auth" ? "" : sipAuthIp;
        this.editForm.callLimit = callLimit;
        this.editForm.emailRecording = emailRecording;
        this.editForm.trunkCidSource = trunkCidSource;

        this.activePeerName = peerName;
        this.activePeerSecret = peerSecret;
        this.enable911Override = trunkEmergencyCidOverride !== "none";

        this.editForm.faxDirect = faxDirect === 1;
        if (email && mailToFax) {
          this.editForm.enableOutboundFaxing = true;
          const { id, cidnum } = mailToFax;
          this.editForm.faxCidNum = cidnum;
          this.editForm.mailToFaxId = id;
        } else {
          this.editForm.faxCidNum = "";
        }

        this.editForm.doNotCalls.push(
          ...de_destinations
            .filter(({ typeSrc }) => typeSrc === "EXT-DONOTCALL")
            .map(({ typeIdDst }) => typeIdDst)
        );

        // ⭐️ Destinations ⭐️ //
        this.editForm.busyDestinations = busyDestinations;
        this.editForm.offlineDestinations = offlineDestinations;
        this.editForm.noAnswerDestinations = noAnswerDestinations;
        this.editForm.forwardingDestinations = forwardingDestinations;

        // Device
        if (device && device.phPmId) {
          this.editForm.device = device;
          this.extHasDevice = true;
        }
        // Handling Music on Hold
        this.editForm.muId = muId;

        // Handle dialTiemout
        if (dialTimeout === 0) {
          feathersClient
            .service("tenants")
            .get(tenantId)
            .then((res) => {
              if (res) {
                this.rings = Math.ceil(res.incomingRingsCount / 5);
              }
            }).catch(() => true);
        } else {
          this.rings = Math.ceil(dialTimeout / 5);
        }

        this.editForm.prefix = prefix;
        if (email) {
          this.email = email;
          this.initialEmail = email;
          this.editForm.email = email;
        }
        this.editForm.extName = name;
        this.editForm.extNumber = number;
        this.editForm.department = department;
        this.editForm.branchId = branchId;
        this.editForm.cidNum = cidNum;
        if (cidNums.length) {
          this.editForm.cidNums = cidNums.map((t) => t.id);
          this.cidNums = cidNums.map((t) => {
            return {
              key: t.id,
              label: t.number,
              disabled: false,
            };
          });
        }

        this.editForm.dnd = !(dnd === "");
        this.forwardAllCalls = unconditionalStatus === "on";
        this.editForm.prefix = prefix;
        this.editForm.recording = recording;
      }
    },

    rings(value) {
      this.editForm.dialTimeout = value * 5;
    },
  },
  methods: {
    checkName(value) {
      const newName = value.normalize('NFD');
      return newName.replace(/[^a-zA-Z0-9\s]/g, '');
    },
    getRandomKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      return `${randomString()}-${randomString()}$${randomString()}`;
    },

    onForwardingDestSelection(destinations) {
      this.editForm.forwardingDestinations = [...destinations];
    },

    onBusyDestinationsSelection(destinations) {
      this.editForm.busyDestinations = [...destinations];
    },

    onOfflineDestinationsSelection(destinations) {
      this.editForm.offlineDestinations = [...destinations];
    },

    onNoAnswerDestinationsSelection(destinations) {
      this.editForm.noAnswerDestinations = [...destinations];
    },

    cancelOperation() {
      this.$emit("cancelOperation", this.editForm);
    },

    validateDestinations(destinations, type) {
      if (destinations.length > 1 && destinations.find((dst) => !dst.id)) {
        this.$notify({
          title: `Empty ${type} Destination field`,
          message: "Don't leave any destination field to be empty.",
          type: "error",
        });
        return;
      }

      let finalDestinations = [];
      if (destinations.length === 1 && destinations[0].id === null) {
        return finalDestinations;
      }

      if (destinations.length) {
        finalDestinations = destinations.map(({ id, type }, idx) => ({
          id,
          type,
          order: idx + 1,
        }));
      }
      return finalDestinations;
    },

    editOperation() {
      // Validate fax outbound caller id is a valid phone number
      if (this.editForm.enableOutboundFaxing && this.editForm.faxCidNum) {
        if (!isValidPhoneNumber(this.editForm.faxCidNum)) {
          this.$notify({
            title: "Invalid Fax Caller ID",
            message: "Fax outbound caller id must be a valid phone number.",
            type: "error",
          });
          return;
        }
      }

      // ⭐️  Notify User if email has Changed ⭐️ //
      if (this.initialEmail !== "") {
        if (this.editForm.email !== this.initialEmail)
          this.$notify({
            title: "Warning! Email has been changed.",
            message:
              "If this happens it would effectively disconnect the existing email's omnia user from the extension.",
            type: "warning",
          });
      }

      if (this.editForm.callLimit === null) {
        this.editForm.callLimit = 5;
      }
      this.editForm.unconditionalStatus = this.forwardAllCalls ? "on" : "";
      this.editForm.deviceType = this.deviceType;
      this.editForm.extHasDevice = this.extHasDevice;

      let editBody = {
        ...this.editForm,
        extType: this.editExtType,
        bulkPatch: false,
      };

      //set mwi field based off setting
      editBody.mailbox = this.editForm.mailbox
        ? `${editBody.extNumber}@${this.selectedTenant.tenantCode}`
        : "";

      //set up meet field properly
      if (this.editExtType !== "desk") {
        editBody.enableMeet = 0;
      }

      //make sure enable meet is posted as int
      editBody.enableMeet = editBody.enableMeet ? 1 : 0;
      editBody.enableTranscription = editBody.enableTranscription ? 1 : 0;
      editBody.recording =
        editBody.recording === "" ? "no" : editBody.recording;

      if (this.editExtType === "sip") {
        editBody.trunkEmergencyOverride = this.enable911Override
          ? "yes"
          : "none";
      }

      // ⭐️ Validate Destinations ⭐️ //
      const busyDestinations = this.validateDestinations(
        editBody.busyDestinations,
        "EXT-BUSY"
      );
      const offlineDestinations = this.validateDestinations(
        editBody.offlineDestinations,
        "EXT-OFFLINE"
      );
      const noAnswerDestinations = this.validateDestinations(
        editBody.noAnswerDestinations,
        "EXT-NOANSWER"
      );
      const forwardingDestinations = this.validateDestinations(
        editBody.forwardingDestinations,
        "EXT-UNCONDITIONAL"
      );

      editBody.faxDirect = editBody.faxDirect ? 1 : 0;

      if (
        (busyDestinations,
        offlineDestinations,
        noAnswerDestinations,
        forwardingDestinations)
      ) {
        editBody.busyDestinations = busyDestinations;
        editBody.offlineDestinations = offlineDestinations;
        editBody.noAnswerDestinations = noAnswerDestinations;
        editBody.forwardingDestinations = forwardingDestinations;
      } else {
        return;
      }

      // ⭐️ Make sure includeInDbn & includeInPb is right ⭐️ //
      editBody.includeInDbn = editBody.includeInDbn ? "yes" : "no";
      editBody.includeInPb = editBody.includeInPb ? "yes" : "no";

      this.$emit("editOperation", editBody);
    },

    clearFields(form) {
      Object.keys(form).forEach((key) => {
        if (typeof form[key] === "string") {
          form[key] = "";
        }
        if (typeof form[key] === "boolean") {
          form[key] = false;
        }
        if (Array.isArray(form[key])) {
          form[key] = [];
        }
      });
      this.collapseActive = false;
      this.enablee911Override = false;
      this.activePeerName = "";
      this.activePeerSecret = "";
      this.cidNums = [];
      this.initialEmail = "";
      this.email = "";
      this.activeTabName = "General";
      this.rings = 0;
      this.emitCheckMac = false;
      this.initialMac = "";
      this.emitCheckExtNum = false;
      this.initialExtNum = "";
      this.forwardAllCalls = false;

      this.editForm.callLimit = null;
      this.editForm.dialTimeout = null;
      this.extHasDevice = false;

      this.deviceType = "";
      this.editForm.newDevice = {
        name: "",
        mac: "",
        phPmId: null,
      };
      this.editForm.existingDevice = {
        id: null,
      };
      this.editForm.device = {
        id: null,
        name: "",
        mac: "",
        phPmId: null,
      };
      this.editForm.emergencyAddress = null;
    },

    remoteFaxOutboundCid(query) {
      this.$emit("remoteFaxOutboundCid", query);
    },

    remoteMethodForOutboundCid(query) {
      this.$emit("remoteMethodForOutboundCid", query);
    },

    numberFormatter(val) {
      return phoneNumberFormatter(val);
    },

    queryRegistrations() {
      this.deviceTableLoading = true;
      this.deviceTableData = [];
      feathersClient
        .service("sbc-management")
        .create({
          command: "kamExtension",
          peerName: this.activePeerName,
        })
        .then((res) => {
          const { code } = res;

          if (!code) {
            this.deviceTableData = res.map((item) => ({
              agent: item.Contact["User-Agent"],
              expires: item.Contact.Expires,
              address: item.Contact.Address,
              received: item.Contact.Received,
              path: item.Contact.Path,
              serverId: item.Contact["Server-Id"],
              lastKeepAlive: item.Contact["Last-Keepalive"],
            }));
          } else {
            this.$message({
              message: "No registered devices found!",
              duration: 1000,
            });
          }
          this.deviceTableLoading = false;
        })
        .catch(() => {
          this.deviceTableData = [];
          this.deviceTableLoading = false;
        });
    },

    updatePeerSecret(val) {
      this.activePeerSecret = val;
    },
  },
  filters: {
    formatNumber(value) {
      return phoneNumberFormatter(value);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.checkbox-group {
  max-width: 600px;
  margin: auto;
}

.el-checkbox {
  margin-left: 0;
}

.el-tabs {
  max-width: 600px;
  margin: auto;
}

.dashboard-button-group {
  display: flex;
  align-items: center;
  justify-content: center;
}

.dashboard-button {
  text-align: center;
  padding: 18px;
}

.inline-form-group .el-col .el-select {
  margin: 5px auto;
}

.address-tag {
  height: auto;

  .address-label {
    margin-top: 0;
    margin-bottom: -10px;
  }

  .coordinates-label {
    margin-top: -10px;
    margin-bottom: 0;
  }
}
</style>
